import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { getDiscipline } from "@/lib/trainingDisciplines";
import DisciplineBadge from "@/components/training/DisciplineBadge";

function exercisePreviewLabel(ex) {
  if (ex.target_weight_kg > 0) {
    const name = (ex.lift_label || ex.name)?.slice(0, 18);
    const pct = ex.intensity_pct ? ` (${ex.intensity_pct}%)` : "";
    return `${name} ${ex.target_weight_kg} kg${pct}`;
  }
  if (ex.target_pace_label) {
    return `${(ex.name || "Course").slice(0, 18)} @ ${ex.target_pace_label}`;
  }
  if (ex.target_power_w > 0) {
    const pct = ex.target_pct_ftp ? ` (${ex.target_pct_ftp}% FTP)` : "";
    return `${(ex.name || "Vélo").slice(0, 18)} ${ex.target_power_w} W${pct}`;
  }
  if (ex.distance_km) {
    return `${(ex.name || "Bloc").slice(0, 18)} ${ex.distance_km} km`;
  }
  return (ex.name || "Exercice").slice(0, 22);
}

function hasPreviewTarget(ex) {
  return (
    (ex.target_weight_kg ?? 0) > 0
    || ex.target_pace_label
    || (ex.target_power_w ?? 0) > 0
    || ex.distance_km
  );
}

export default function TomorrowLoadsCard() {
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/workouts/tomorrow")
      .then((r) => setPreview(r.data))
      .catch(() => setPreview(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !preview?.has_session) return null;

  const discipline = preview.discipline || "strength";
  const meta = getDiscipline(discipline);
  const Icon = meta.icon;
  const withTargets = (preview.exercises || []).filter(hasPreviewTarget);

  return (
    <Card className={`mb-6 border-l-4 ${meta.border} ${meta.light} bg-opacity-30`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${meta.light}`}>
              <Icon className={`w-5 h-5 ${meta.accent}`} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold text-slate-900">
                  Demain — {preview.day_name}
                </p>
                <DisciplineBadge discipline={discipline} size="sm" />
              </div>
              {preview.plan_title && (
                <p className="text-xs text-slate-500 mt-0.5 truncate">{preview.plan_title}</p>
              )}
              {withTargets.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {withTargets.slice(0, 5).map((ex, i) => (
                    <Badge key={i} variant="secondary" className={`text-[10px] bg-white ${meta.border} ${meta.accent}`}>
                      {exercisePreviewLabel(ex)}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-600 mt-2">
                  {(preview.exercises || []).length} bloc(s) prévu(s) — consultez votre programme.
                </p>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className={`shrink-0 rounded-full gap-1 text-xs ${meta.border} ${meta.accent} hover:${meta.light}`}
            onClick={() => navigate("/student/workout-log")}
          >
            Loguer <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}